import React, { useMemo, useState } from 'react';
import {
  RadialBarChart as RechartsRadialBarChart,
  RadialBar,
  Legend,
  Tooltip,
  ResponsiveContainer,
  PolarAngleAxis,
  PolarGrid,
} from 'recharts';
import { ChartContainer, ChartConfig } from './ChartContainer';
import { ChartWrapper } from './ChartWrapper';
import { ChartTooltipContent } from './ChartTooltip';
import { ChartLegendContent } from './ChartLegend';
import { defaultPalette } from '../../lib/chartPalette';
import { sanitizeForCSSVar } from '../../lib/chartCssUtils';

export interface RadialChartProps {
  data: any[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  dataKey: string;
  nameKey?: string;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: any) => string;
  innerRadius?: number | string;
  outerRadius?: number | string;
  startAngle?: number;
  endAngle?: number;
  label?: boolean;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view */
  isExpanded?: boolean;
}

// Helper to auto-generate config from data
const useChartDefaults = (
  data: any[],
  nameKey: string,
  providedConfig?: ChartConfig
) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return providedConfig || {};
    }

    // Use provided config or auto-generate with default (violet) palette colors
    const config = providedConfig || data.reduce((acc, item, index) => {
      const name = item[nameKey];
      acc[name] = {
        label: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize first letter
        color: defaultPalette[index % defaultPalette.length],
      };
      return acc;
    }, {} as ChartConfig);

    return config;
  }, [data, nameKey, providedConfig]);
};

export const RadialChart: React.FC<RadialChartProps> = ({
  data,
  config: providedConfig,
  title,
  description,
  dataKey,
  nameKey = 'name',
  showTooltip = true,
  showLegend = true,
  height = 350,
  className,
  tooltipFormatter,
  innerRadius = '10%',
  outerRadius = '80%',
  startAngle = 90,
  endAngle = -270,
  label = false,
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
}) => {
  const config = useChartDefaults(data, nameKey, providedConfig);
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);

  // In expanded view, increase radius for better visibility
  const effectiveOuterRadius = isExpanded ? '90%' : outerRadius;
  const effectiveInnerRadius = isExpanded ? '20%' : innerRadius;

  const chartContent = (
    <ChartContainer config={config}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsRadialBarChart
          cx="50%"
          cy="50%"
          innerRadius={effectiveInnerRadius}
          outerRadius={effectiveOuterRadius}
          data={data}
          startAngle={startAngle}
          endAngle={endAngle}
        >
          <PolarGrid
            gridType="circle"
            stroke="hsl(var(--border))"
            strokeOpacity={0.3}
          />
          <PolarAngleAxis
            type="number"
            domain={[0, 100]}
            angleAxisId={0}
            tick={false}
          />
          {data.map((entry, index) => (
            <RadialBar
              key={`radial-bar-${index}`}
              dataKey={dataKey}
              cornerRadius={10}
              label={label ? { position: 'insideStart', fill: '#fff' } : false}
              fill={
                config[entry[nameKey]]?.color ||
                `var(--color-${sanitizeForCSSVar(entry[nameKey])})`
              }
              opacity={
                hoveredDataKey === null || hoveredDataKey === entry[nameKey] ? 1 : 0.3
              }
              background={{ fill: 'hsl(var(--muted))', opacity: 0.2 }}
            />
          ))}
          {showTooltip && (
            <Tooltip
              content={
                <ChartTooltipContent
                  valueFormatter={tooltipFormatter}
                  hideIndicator
                />
              }
            />
          )}
          {showLegend && (
            <Legend
              iconSize={10}
              layout="vertical"
              verticalAlign="middle"
              align="right"
              content={
                <ChartLegendContent
                  onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                  onMouseLeave={() => setHoveredDataKey(null)}
                />
              }
            />
          )}
        </RechartsRadialBarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={RadialChart}
      chartProps={{
        data,
        config: providedConfig,
        dataKey,
        nameKey,
        showTooltip,
        showLegend,
        height,
        tooltipFormatter,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        label,
      }}
    >
      {chartContent}
    </ChartWrapper>
  );
};
